import { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { safeRedirectPath } from '@mockd/shared';
import { useAuth } from '../hooks';
import { ROUTES } from '../routes';
import { Alert, GitHubMark, MockdLogo } from '../components/common';
import { MagicLinkForm, MagicLinkSent } from '../components/auth';

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, loading, error, clearError, loginWithGitHub, magicLinkSent, resetMagicLink } = useAuth();

  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState<'magic' | 'password'>('magic');
  const [oauthError, setOauthError] = useState<string | null>(null);

  const redirect = searchParams.get('redirect') ?? undefined;
  const from =
    safeRedirectPath(redirect) ??
    (location.state as { from?: { pathname: string } })?.from?.pathname ??
    '/';

  // Clear any stale sent-state from a previous visit so the form (and any
  // error from the URL) is visible again
  useEffect(() => {
    resetMagicLink();
  }, [resetMagicLink]);

  // Handle OAuth/magic link error from URL params
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      const errorMessages: Record<string, string> = {
        oauth_denied: 'GitHub authorization was denied.',
        invalid_callback: 'Invalid OAuth callback.',
        invalid_state: 'Invalid OAuth state. Please try again.',
        state_expired: 'OAuth session expired. Please try again.',
        token_exchange_failed: 'Failed to authenticate with GitHub.',
        no_email: 'Could not retrieve a verified primary email from GitHub. Please verify your primary email address on GitHub and try again.',
        invalid_token: 'Invalid magic link. Please request a new one.',
        token_used: 'This magic link has already been used.',
        token_expired: 'This magic link has expired.',
      };
      setOauthError(errorMessages[errorParam] || 'An error occurred. Please try again.');
    }
  }, [searchParams]);

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setOauthError(null);

    try {
      await login({ email, password });
      navigate(from, { replace: true });
    } catch {
      // Error is handled by useAuth
    }
  };

  const handleGitHubLogin = () => {
    clearError();
    setOauthError(null);
    loginWithGitHub(safeRedirectPath(redirect) ?? undefined);
  };

  const toggleMode = () => {
    clearError();
    setOauthError(null);
    setMode(mode === 'magic' ? 'password' : 'magic');
  };

  return (
    <div className="flex-1 bg-base-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <MockdLogo size="md" className="mx-auto" />
          <h1 className="mt-4 text-base-content">Sign in to your account</h1>
        </div>

        <div className="p-6">
          {magicLinkSent ? (
            <MagicLinkSent linkLabel="sign-in link" />
          ) : (
            <>
              {(error || oauthError) && (
                <Alert variant="error" className="mb-4">{error || oauthError}</Alert>
              )}

              {/* GitHub OAuth Button */}
              <button
                onClick={handleGitHubLogin}
                disabled={loading}
                className="btn btn-block btn-outline"
              >
                <GitHubMark />
                Continue with GitHub
              </button>

              <div className="divider">or</div>

              {mode === 'magic' ? (
                <MagicLinkForm
                  email={email}
                  onEmailChange={setEmail}
                  buttonLabel="Send magic link"
                  onBeforeSubmit={() => setOauthError(null)}
                  redirect={safeRedirectPath(redirect) ?? undefined}
                />
              ) : (
                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  <div className="fieldset">
                    <label htmlFor="email" className="fieldset-legend">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      className="input w-full"
                      placeholder="you@example.com"
                    />
                  </div>

                  <div className="fieldset">
                    <label htmlFor="password" className="fieldset-legend">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete="current-password"
                        className="input w-full pr-10"
                        placeholder="Enter your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/70 hover:text-base-content"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOffIcon className="size-5" /> : <EyeIcon className="size-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="text-right">
                    <Link to="/forgot-password" className="link link-hover text-sm">
                      Forgot password?
                    </Link>
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn btn-primary w-full"
                    >
                      {loading ? 'Signing in...' : 'Sign in'}
                    </button>
                  </div>

                </form>
              )}

              <div className="mt-4 text-center text-sm">
                <button onClick={toggleMode} className="link link-primary">
                  {mode === 'magic' ? 'Use password instead' : 'Use a magic link instead'}
                </button>
              </div>

              <div className="mt-4 text-center text-sm text-base-content/80">
                Don't have an account?{' '}
                <Link
                  to={{ pathname: ROUTES.auth.register(), search: searchParams.toString() }}
                  className="link link-primary"
                >
                  Sign up
                </Link>
              </div>
            </>
          )}
        </div>

        <div className="mt-4 text-center">
          <Link to={ROUTES.home()} className="link link-hover text-sm text-base-content/80">
            Continue without signing in
          </Link>
        </div>
      </div>
    </div>
  );
}
